import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, MapPin, IndianRupee, Clock, PlusCircle, Search,
  Compass, Landmark, User, Mail, Sparkles, Building2, CheckCircle2,
  CalendarDays
} from "lucide-react";
import { motion } from "framer-motion";
import { api, formatPrice, asItems, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

const STATUS_COLORS = {
  new: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
  matching: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/20",
  assigned: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
  completed: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const CATEGORY_ICONS = {
  "property land": Landmark,
  "house / flat": Building2,
  "villa": Sparkles,
  "farm house": Compass
};

const DEMAND_TABS = [
  { id: "all", label: "All Requests" },
  { id: "new", label: "New / Pending" },
  { id: "assigned", label: "Assigned" },
  { id: "completed", label: "Completed" }
];

export default function PlotDemandsDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  
  // Filtering states
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    api.get("/v1/plot-demands/me")
      .then(({ data }) => { if (live) setItems(asItems(data)); })
      .catch((requestError) => {
        if (live) {
          setItems([]);
          setError(formatApiError(requestError));
        }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [reloadKey]);

  // Filter items locally
  const filteredItems = items.filter(d => {
    const status = String(d.status || "NEW").toLowerCase();
    const matchesTab = activeTab === "all" || status === activeTab;

    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = query === "" ||
      String(d.location || "").toLowerCase().includes(query) ||
      String(d.category || "").toLowerCase().includes(query) ||
      String(d.buyer_note || "").toLowerCase().includes(query) ||
      String(d.agent_name || "").toLowerCase().includes(query);

    return matchesTab && matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="plot-demands-dashboard-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Buyer Sourcing</p>
          <h1 className="font-display text-4xl font-black tracking-tighter text-primary">My Plots on Demand</h1>
          <p className="text-muted-foreground mt-2">Track requirements submitted for admin and verified agent matching.</p>
        </div>
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full shrink-0 md:w-auto"
        >
          <Button
            asChild
            className="w-full md:w-auto font-bold gap-2 rounded-xl shadow-lg bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-700 hover:via-indigo-700 hover:to-violet-700 text-white border-0 py-5 px-6 shadow-indigo-500/10 hover:shadow-indigo-500/25 transition-all duration-300 relative overflow-hidden group"
            data-testid="new-plot-demand-btn"
          >
            <Link to="/plot-on-demand">
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
              <PlusCircle className="w-4 h-4 shrink-0" />
              <span>New request</span>
            </Link>
          </Button>
        </motion.div>
      </div>

      {/* Dynamic Status Tabs & Search Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border pb-4">
        <div className="w-full lg:w-auto bg-card border border-border/80 p-1 rounded-xl overflow-x-auto scrollbar-none relative flex gap-1 shrink-0">
          {DEMAND_TABS.map(tab => {
            const isActive = activeTab === tab.id;
            const tabCount = items.filter(d => tab.id === "all" || String(d.status || "NEW").toLowerCase() === tab.id).length;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 lg:flex-none text-center px-4 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 relative z-10 ${
                  isActive ? "text-white" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`demand-tab-${tab.id}`}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeDemandTab"
                    className="absolute inset-0 bg-primary rounded-lg z-[-1] shadow-sm"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  />
                )}
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-tight ${
                  isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground"
                }`}>
                  {tabCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search input bar */}
        <div className="relative w-full lg:w-64">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by location, agent..."
            className="pl-10 pr-4 py-2 rounded-xl text-sm w-full bg-background border-border"
          />
          <Search className="w-4 h-4 text-muted-foreground absolute left-3.5 top-3" />
        </div>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-44 shimmer rounded-2xl border border-border/60" />
          ))}
        </div>
      ) : error ? (
        <Card className="rounded-3xl border border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
          <ClipboardList className="w-10 h-10 mx-auto text-destructive/70 mb-3" />
          <h3 className="font-display text-xl font-bold text-primary">Requests could not be loaded</h3>
          <p className="text-sm text-muted-foreground mt-2 mb-5">{error}</p>
          <Button type="button" variant="outline" onClick={() => setReloadKey((key) => key + 1)}>
            Try again
          </Button>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="rounded-3xl border border-dashed border-border bg-card p-12 text-center" data-testid="plot-demands-empty">
          <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="font-display text-2xl font-bold text-primary">No matching requests</h3>
          <p className="text-muted-foreground mt-2 mb-5 max-w-sm mx-auto">
            You don't have any sourcing requests under this status. Create a new request to find matching properties.
          </p>
          <Button asChild className="font-bold rounded-xl px-5 py-2.5"><Link to="/plot-on-demand">Open Plots on Demand</Link></Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-5" data-testid="plot-demands-list">
          {filteredItems.map((d) => {
            const statusLabel = String(d.status || "NEW").toUpperCase();
            const IconComponent = CATEGORY_ICONS[String(d.category || "").toLowerCase()] || Landmark;
            return (
              <Card 
                key={d.id || d.demand_id || d.plot_demand_id} 
                className="rounded-3xl border border-border bg-card p-5 sm:p-6 flex flex-col justify-between hover:shadow-md transition duration-200"
              >
                <div className="space-y-4">
                  {/* Card Header status and date */}
                  <div className="flex items-center justify-between gap-3 border-b border-dashed border-border/80 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <IconComponent className="w-4.5 h-4.5" />
                      </div>
                      <span className="text-xs font-bold text-foreground capitalize">
                        {d.category || "Plot / Land"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`font-bold px-2 py-0.5 rounded-full border ${
                        STATUS_COLORS[statusLabel.toLowerCase()] || STATUS_COLORS.new
                      }`}>
                        {statusLabel}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                        <CalendarDays className="w-3.5 h-3.5" />
                        {d.created_at ? new Date(d.created_at).toLocaleDateString("en-IN") : "Date unavailable"}
                      </span>
                    </div>
                  </div>

                  {/* Body Content */}
                  <div>
                    <h3 className="font-display text-xl font-black text-primary leading-tight">
                      {d.area || d.area_min || "Any"} {d.area_unit || "sqyd"} Requirement
                    </h3>
                    
                    <div className="mt-3.5 space-y-2 text-xs font-medium text-muted-foreground">
                      <div className="flex items-center gap-2 text-foreground">
                        <MapPin className="w-4 h-4 text-primary/70 shrink-0" />
                        <span>{d.location || [d.locality, d.city].filter(Boolean).join(", ") || "Location flexible"}</span>
                      </div>
                      <div className="flex items-center gap-2 text-foreground">
                        <IndianRupee className="w-4 h-4 text-primary/70 shrink-0" />
                        <span>Budget up to {formatPrice(d.budget_max || d.budget)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary/70 shrink-0" />
                        <span>Timeframe: <strong className="text-foreground capitalize">{String(d.timeframe || "Flexible").toLowerCase().replace(/_/g, " ")}</strong></span>
                      </div>
                    </div>
                  </div>

                  {/* Notes box */}
                  {d.buyer_note && (
                    <div className="p-3 bg-secondary/35 border border-border/60 rounded-2xl text-xs text-muted-foreground leading-relaxed italic">
                      &ldquo;{d.buyer_note}&rdquo;
                    </div>
                  )}
                </div>

                {/* Agent Assignment Info */}
                {d.agent_name && (
                  <div className="mt-4 pt-4 border-t border-dashed border-border/80 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <User className="w-3.5 h-3.5 text-primary/70" />
                      <span>Assigned Agent: <strong className="text-foreground font-semibold">{d.agent_name}</strong></span>
                    </div>
                    {d.agent_email && (
                      <a 
                        href={`mailto:${d.agent_email}`} 
                        className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        <Mail className="w-3.5 h-3.5" /> Contact
                      </a>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
